import { ApiError } from './generated/api-client.js'

export type ApiErrorPayload = {
  status?: string
  message: string
  [key: string]: unknown
}

/** The status prefix is emitted from the real HTTP response, not its JSON body. */
export function parseApiErrorStatus(error: unknown): number | null {
  if (error instanceof ApiError) return error.status
  if (!(error instanceof Error)) return null
  const match = /^API request failed \[(\d{3})\](?:\s|$)/u.exec(error.message)
  return match ? Number(match[1]) : null
}

function stripApiErrorPrefix(message: string): string {
  return message.replace(/^API request failed \[\d+\]\s*/u, '').trim()
}

export function parseApiErrorPayload(error: unknown): ApiErrorPayload | null {
  if (!(error instanceof Error)) return null
  if (error instanceof ApiError) {
    const payload = error.payload
    const message =
      typeof payload?.message === 'string' && payload.message.trim()
        ? payload.message
        : error.rawBody.trim() || 'No response body'
    return { ...payload, message }
  }
  const trimmed = stripApiErrorPrefix(error.message)
  if (!trimmed) {
    return { message: error.message }
  }
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>
    if (parsed && typeof parsed === 'object') {
      const message =
        typeof parsed.message === 'string' && parsed.message.trim().length > 0
          ? parsed.message
          : error.message
      return { ...parsed, message }
    }
  } catch {
    // ignore parse failures and fall through
  }
  return { message: trimmed }
}

export function parseApiErrorMessage(error: unknown, fallback = 'Unknown error'): string {
  const payload = parseApiErrorPayload(error)
  if (!payload) return fallback
  return payload.message || fallback
}
